import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Code, Copy, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export default function Integration() {
  const [copiedId, setCopiedId] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const CodeBlock = ({ code, language = "bash", id }) => (
    <div className="relative bg-zinc-900 rounded-lg p-3 mt-2">
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 p-1.5 hover:bg-zinc-700 rounded transition-colors"
        data-testid={`copy-${id}`}
      >
        {copiedId === id ? (
          <CheckCircle2 size={14} className="text-emerald-400" />
        ) : (
          <Copy size={14} className="text-zinc-400" />
        )}
      </button>
      <pre className="text-xs text-zinc-300 overflow-x-auto pr-8">
        <code>{code}</code>
      </pre>
    </div>
  );

  return (
    <div className="p-4" data-testid="integration-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-zinc-950">
          API Integration
        </h2>
        <p className="text-xs text-zinc-600 mt-0.5">
          Integrate fraud detection into your payment systems
        </p>
      </div>

      <div className="space-y-4">
        {/* Base URL */}
        <Card className="p-4 bg-white border border-zinc-200">
          <h3 className="text-sm font-semibold text-zinc-950 mb-2">Base URL</h3>
          <CodeBlock 
            code={`${BACKEND_URL}/api`} 
            id="base-url" 
          />
        </Card>

        {/* Real-time Prediction */}
        <Card className="p-4 bg-white border border-zinc-200">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-blue-100 text-blue-700 text-[10px]">POST</Badge>
            <h3 className="text-sm font-semibold text-zinc-950">/predict</h3>
          </div>
          <p className="text-xs text-zinc-600 mb-3">
            Submit a transaction for real-time fraud detection. Returns fraud score, risk level, and block reason.
          </p>
          
          <div className="mb-3">
            <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-1">Request</p>
            <CodeBlock
              id="predict-request"
              code={`curl -X POST "${BACKEND_URL}/api/predict" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 2500.00,
    "merchant": "Unknown Store",
    "category": "online",
    "card_last_4": "4242",
    "location": "Nigeria"
  }'`}
            />
          </div>

          <div>
            <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-1">Response</p>
            <CodeBlock
              id="predict-response"
              code={`{
  "transaction_id": "TXN123ABC456",
  "fraud_score": 0.92,
  "risk_level": "high",
  "status": "blocked",
  "block_reason": "Unusually high transaction amount"
}`}
            />
          </div>
        </Card>

        {/* Train Model */}
        <Card className="p-4 bg-white border border-zinc-200">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-blue-100 text-blue-700 text-[10px]">POST</Badge>
            <h3 className="text-sm font-semibold text-zinc-950">/train</h3>
          </div>
          <p className="text-xs text-zinc-600 mb-3">
            Train the fraud detection model with sample data.
          </p>
          <CodeBlock
            id="train-request"
            code={`curl -X POST "${BACKEND_URL}/api/train"`}
          />
        </Card>

        {/* Dashboard Stats */}
        <Card className="p-4 bg-white border border-zinc-200">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">GET</Badge>
            <h3 className="text-sm font-semibold text-zinc-950">/dashboard/stats</h3>
          </div>
          <p className="text-xs text-zinc-600 mb-3">
            Get real-time fraud detection statistics.
          </p>
          <CodeBlock
            id="stats-request"
            code={`curl "${BACKEND_URL}/api/dashboard/stats"`}
          />
          <div className="mt-3">
            <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-1">Response</p>
            <CodeBlock
              id="stats-response"
              code={`{
  "total_transactions": 10234,
  "fraud_detected": 156,
  "fraud_rate": 1.52,
  "total_blocked": 142
}`}
            />
          </div>
        </Card>

        {/* Model Status */}
        <Card className="p-4 bg-white border border-zinc-200">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">GET</Badge>
            <h3 className="text-sm font-semibold text-zinc-950">/model/status</h3>
          </div>
          <p className="text-xs text-zinc-600 mb-3">
            Get current model performance metrics.
          </p>
          <CodeBlock
            id="model-status"
            code={`curl "${BACKEND_URL}/api/model/status"`}
          />
        </Card>

        {/* Alerts */}
        <Card className="p-4 bg-white border border-zinc-200">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">GET</Badge>
            <h3 className="text-sm font-semibold text-zinc-950">/alerts</h3>
          </div>
          <p className="text-xs text-zinc-600 mb-3">
            Get recent fraud alerts with block reasons.
          </p>
          <CodeBlock
            id="alerts-request"
            code={`curl "${BACKEND_URL}/api/alerts?limit=10"`}
          />
        </Card>

        {/* WebSocket */}
        <Card className="p-4 bg-white border border-zinc-200">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-purple-100 text-purple-700 text-[10px]">WS</Badge>
            <h3 className="text-sm font-semibold text-zinc-950">/api/ws/transactions</h3>
          </div>
          <p className="text-xs text-zinc-600 mb-3">
            WebSocket endpoint for real-time transaction streaming.
          </p>
          <CodeBlock
            id="websocket-example"
            code={`const ws = new WebSocket("wss://your-domain/api/ws/transactions");

ws.onmessage = (event) => {
  const data = JSON.parse(event.data);
  if (data.type === 'transaction') {
    console.log('New transaction:', data.data);
  }
  if (data.type === 'alert') {
    console.log('Fraud alert:', data.data);
  }
};`}
          />
        </Card>

        {/* Hugging Face Integration Guide */}
        <Card className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200" data-testid="hf-integration-guide">
          <div className="flex items-center gap-2 mb-4">
            <img src="https://huggingface.co/front/assets/huggingface_logo-noborder.svg" alt="HF" className="w-6 h-6" />
            <h3 className="text-lg font-bold text-amber-900">ML Model Integration</h3>
          </div>
          
          <p className="text-xs text-amber-800 mb-4">
            Two options for ML-based fraud detection. Setup is done on the <strong>Training</strong> page.
          </p>

          <div className="space-y-4">
            {/* Train with Emergent */}
            <div className="p-3 bg-white border border-amber-200 rounded-lg">
              <h4 className="text-sm font-semibold text-amber-900 mb-2">Option 1: Train with Emergent</h4>
              <p className="text-xs text-amber-700 mb-2">
                We generate synthetic data and train a Gradient Boosting model for you
              </p>
              <CodeBlock
                id="train-emergent"
                code={`# Train with Emergent (one-click)
curl -X POST "${BACKEND_URL}/api/model/train-emergent"

# Response:
{
  "status": "success",
  "message": "Emergent ML model trained successfully!",
  "model_name": "google/flan-t5-small",
  "training_samples": 10000
}`}
              />
            </div>

            {/* Connect HF Endpoint */}
            <div className="p-3 bg-white border border-amber-200 rounded-lg">
              <h4 className="text-sm font-semibold text-amber-900 mb-2">Option 2: Connect Your Hugging Face Model</h4>
              <p className="text-xs text-amber-700 mb-2">
                Connect your own trained model via Hugging Face Inference Endpoint
              </p>
              <CodeBlock
                id="hf-connect-api"
                code={`# Connect your Hugging Face endpoint
curl -X POST "${BACKEND_URL}/api/model/connect-hf" \\
  -H "Content-Type: application/json" \\
  -d '{
    "hf_endpoint_url": "https://xxxx.us-east-1.aws.endpoints.huggingface.cloud"
  }'

# Response:
{
  "status": "success",
  "hf_enabled": true
}`}
              />
            </div>

            {/* Check Status */}
            <div className="p-3 bg-white border border-amber-200 rounded-lg">
              <h4 className="text-sm font-semibold text-amber-900 mb-2">Check Model Status</h4>
              <p className="text-xs text-amber-700 mb-2">
                Check current detection mode
              </p>
              <CodeBlock
                id="model-status"
                code={`# Get current model status
curl "${BACKEND_URL}/api/model/status"

# Response:
{
  "detection_mode": "emergent_ml",  // or "huggingface_ml" or "rule_based"
  "model_name": "google/flan-t5-small",
  "accuracy": 0.92,
  "precision": 0.90,
  "recall": 0.88
}`}
              />
            </div>

            {/* Disconnect */}
            <div className="p-3 bg-white border border-amber-200 rounded-lg">
              <h4 className="text-sm font-semibold text-amber-900 mb-2">Disconnect ML Model</h4>
              <p className="text-xs text-amber-700 mb-2">
                Revert to rule-based detection
              </p>
              <CodeBlock
                id="model-disconnect"
                code={`# Disconnect and revert to rule-based
curl -X POST "${BACKEND_URL}/api/model/disconnect"

# Response:
{
  "status": "success",
  "message": "Disconnected. Using rule-based detection."
}`}
              />
            </div>
          </div>
        </Card>

        {/* Integration Help */}
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <div className="flex items-start gap-3">
            <Code size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-blue-900 mb-1">Need Help?</p>
              <p className="text-xs text-blue-700">
                For cloud training options, visit the Training page. For webhook setup and advanced integration, contact our team.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
